import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, ChevronRight, ArrowLeft } from 'lucide-react';
import { faqData } from '../data/faqData';

interface InlineFAQProps {
  /** The sub-module ID, e.g. 'module-1-sub-4'. Use 'module-all' to show a module picker. */
  subModuleId: string;
  onReturnToMainMenu?: () => void;
}

const FAQS_PER_PAGE = 4;

// ── Module picker (shown for 'module-all') ─────────────────────────────────
const MODULE_COLORS = [
  'bg-blue-50 text-blue-600 border-blue-100',
  'bg-violet-50 text-violet-600 border-violet-100',
  'bg-emerald-50 text-emerald-600 border-emerald-100',
  'bg-orange-50 text-orange-600 border-orange-100',
  'bg-pink-50 text-pink-600 border-pink-100',
  'bg-cyan-50 text-cyan-600 border-cyan-100',
  'bg-amber-50 text-amber-600 border-amber-100',
  'bg-rose-50 text-rose-600 border-rose-100',
];

const AllModulesPicker: React.FC<{ onReturnToMainMenu?: () => void }> = ({ onReturnToMainMenu }) => {
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null);
  const [openModuleId, setOpenModuleId] = useState<string | null>(null);

  const selectedSub = selectedSubId
    ? faqData.flatMap((m) => m.subModules).find((sm) => sm.id === selectedSubId)
    : null;

  return (
    <div className="flex flex-col gap-2">
      {selectedSub ? (
        <>
          <button
            onClick={() => setSelectedSubId(null)}
            className="flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-700 font-medium mb-1 focus:outline-none"
          >
            ← Back to modules
          </button>
          <SubModuleFaqList subModuleId={selectedSubId} onReturnToMainMenu={onReturnToMainMenu} />
        </>
      ) : (
        <>
          {faqData.map((mod, idx) => {
            const isOpen = openModuleId === mod.id;
            const colorClass = MODULE_COLORS[idx % MODULE_COLORS.length];
            return (
              <div key={mod.id} className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                <button
                  onClick={() => setOpenModuleId(isOpen ? null : mod.id)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors focus:outline-none"
                >
                  <span className={`flex-shrink-0 w-6 h-6 rounded-lg border text-[10px] font-bold flex items-center justify-center ${colorClass}`}>
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-[12px] font-semibold text-gray-800 truncate">
                    {mod.moduleTitle}
                  </span>
                  <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 flex flex-col gap-1.5 border-t border-gray-50">
                    {mod.subModules.map((sm) => (
                      <button
                        key={sm.id}
                        onClick={() => setSelectedSubId(sm.id)}
                        className="flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left text-[11.5px] text-gray-600 hover:bg-blue-50 hover:text-blue-700 border border-transparent hover:border-blue-100 transition-all focus:outline-none"
                      >
                        <span className="truncate">{sm.subModuleTitle}</span>
                        <span className="flex-shrink-0 ml-2 text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{sm.faqs.length}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {onReturnToMainMenu && (
            <div className="pt-2">
              <button
                onClick={onReturnToMainMenu}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-blue-200 bg-blue-50/80 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-fit cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
                Return to Main Menu
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Sub-module FAQ list (reusable) ─────────────────────────────────────────
const SubModuleFaqList: React.FC<{ subModuleId: string; onReturnToMainMenu?: () => void }> = ({
  subModuleId,
  onReturnToMainMenu,
}) => {
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(FAQS_PER_PAGE);

  let subModuleTitle = '';
  let faqs: { id: string; question: string; answer: string }[] = [];

  for (const mod of faqData) {
    for (const sm of mod.subModules) {
      if (sm.id === subModuleId) {
        subModuleTitle = sm.subModuleTitle;
        faqs = sm.faqs;
        break;
      }
    }
    if (faqs.length) break;
  }

  if (!faqs.length) return null;

  const displayedFaqs = faqs.slice(0, visibleCount);
  const remaining = faqs.length - visibleCount;
  const nextBatch = Math.min(remaining, FAQS_PER_PAGE);

  return (
    <div className="flex flex-col gap-1.5">
      {subModuleTitle && (
        <span className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wide">
          FAQs · {subModuleTitle}
        </span>
      )}
      {displayedFaqs.map((faq, idx) => {
        const isOpen = openFaqId === faq.id;
        return (
          <div
            key={faq.id}
            className={`rounded-xl border transition-all duration-200 bg-white ${
              isOpen ? 'border-blue-200 shadow-sm' : 'border-gray-200 hover:border-blue-100'
            }`}
          >
            <button
              onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
              aria-expanded={isOpen}
              aria-controls={`inline-faq-answer-${faq.id}`}
              id={`inline-faq-btn-${faq.id}`}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded-xl"
            >
              <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-50 text-blue-500 text-[9px] font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <span className="flex-1 text-[12px] font-medium text-gray-800 leading-snug">
                {faq.question}
              </span>
              <span className="flex-shrink-0 text-gray-400">
                {isOpen ? (
                  <ChevronUp className="w-3 h-3 text-blue-400" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </span>
            </button>
            <div
              id={`inline-faq-answer-${faq.id}`}
              role="region"
              aria-labelledby={`inline-faq-btn-${faq.id}`}
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-3 pb-3 pt-0 ml-6">
                <div className="border-l-2 border-blue-200 pl-2.5">
                  <p className="text-[11.5px] text-gray-500 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {remaining > 0 && (
        <button
          onClick={() => setVisibleCount((c) => Math.min(c + FAQS_PER_PAGE, faqs.length))}
          className="mt-0.5 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl border border-blue-100 bg-blue-50/60 text-blue-600 text-[11px] font-semibold hover:bg-blue-100 transition-all duration-150 focus:outline-none w-fit"
        >
          <ChevronDown className="w-3 h-3" />
          Show {nextBatch} more &middot; {remaining} remaining
        </button>
      )}
      {visibleCount > FAQS_PER_PAGE && remaining === 0 && (
        <button
          onClick={() => setVisibleCount(FAQS_PER_PAGE)}
          className="mt-0.5 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl border border-gray-100 bg-gray-50 text-gray-400 text-[11px] font-medium hover:bg-gray-100 transition-all duration-150 focus:outline-none w-fit"
        >
          <ChevronUp className="w-3 h-3" />
          Show less
        </button>
      )}

      {onReturnToMainMenu && (
        <div className="pt-2">
          <button
            onClick={onReturnToMainMenu}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-blue-200 bg-blue-50/80 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-fit cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
            Return to Main Menu
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main export ────────────────────────────────────────────────────────────
const InlineFAQ: React.FC<InlineFAQProps> = ({ subModuleId, onReturnToMainMenu }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col w-full mb-4 pl-10"
    >
      {subModuleId === 'module-all' ? (
        <AllModulesPicker onReturnToMainMenu={onReturnToMainMenu} />
      ) : (
        <SubModuleFaqList subModuleId={subModuleId} onReturnToMainMenu={onReturnToMainMenu} />
      )}
    </motion.div>
  );
};

export default InlineFAQ;

